import type { CleanupPolicy, JobFileLinks, JobStatus } from '@/lib/packshots';
import type { StageEvent } from '@/lib/packshotSocket';

export const STAGE_ORDER = ['ingest', 'assess', 'props', 'cleanup', 'cutout', 'compose', 'qa'] as const;
export type StageId = (typeof STAGE_ORDER)[number];
export type RailStatus = 'pending' | 'active' | 'completed' | 'skipped' | 'failed';

export const STAGE_LABEL: Record<StageId, string> = {
  ingest: 'Ingest',
  assess: 'Assess',
  props: 'Remove props',
  cleanup: 'Cleanup',
  cutout: 'Cutout',
  compose: 'Compose',
  qa: 'QA',
};

export type RailJob = {
  id: string;
  status: JobStatus;
  stage: string | null;
  updatedAt: string | null;
  errorStage: string | null;
  files: JobFileLinks;
  sourceMeta: { width: number; height: number } | null;
};

export function visibleStages(policy: CleanupPolicy): StageId[] {
  if (policy === 'always') return [...STAGE_ORDER];
  return STAGE_ORDER.filter((stage) => stage !== 'cleanup');
}

export function railStatuses(job: RailJob, stages: readonly StageId[], events: readonly StageEvent[]): Record<StageId, RailStatus> {
  const statuses = {} as Record<StageId, RailStatus>;
  const terminalOk = job.status === 'completed' || job.status === 'needs_review';
  const failedAt = job.status === 'failed' ? orderIndex(job.errorStage) : -1;
  const cursor = orderIndex(job.stage);

  for (const stage of stages) {
    const index = orderIndex(stage);
    if (terminalOk) {
      statuses[stage] = finishedStatus(stage, job);
      continue;
    }
    if (job.status === 'failed' && failedAt >= 0) {
      if (index < failedAt) statuses[stage] = finishedStatus(stage, job);
      else if (index === failedAt) statuses[stage] = 'failed';
      else statuses[stage] = 'pending';
      continue;
    }
    if (cursor < 0 || (job.status === 'queued' && !job.stage)) {
      statuses[stage] = 'pending';
      continue;
    }
    if (index < cursor) statuses[stage] = finishedStatus(stage, job);
    else if (index === cursor && (job.status === 'processing' || job.status === 'sourcing')) statuses[stage] = 'active';
    else statuses[stage] = 'pending';
  }

  for (const event of events) {
    if (!isStageId(event.stage) || !stages.includes(event.stage)) continue;
    statuses[event.stage] = event.status === 'started'
      ? 'active'
      : event.status === 'completed'
        ? 'completed'
        : event.status === 'skipped'
          ? 'skipped'
          : 'failed';
  }
  return statuses;
}

function finishedStatus(stage: StageId, job: RailJob): RailStatus {
  switch (stage) {
    case 'ingest':
      return job.files.original || job.files.normalized ? 'completed' : 'pending';
    case 'assess':
      return 'completed';
    case 'props':
      return job.files.erased ? 'completed' : 'skipped';
    case 'cleanup':
      return job.files.cleaned ? 'completed' : 'skipped';
    case 'cutout':
      return job.files.cutout ? 'completed' : 'pending';
    case 'compose':
    case 'qa':
      return job.files.output ? 'completed' : 'pending';
    default: {
      const unreachable: never = stage;
      return unreachable;
    }
  }
}

function orderIndex(stage: string | null): number {
  if (!stage) return -1;
  return STAGE_ORDER.indexOf(stage as StageId);
}

function isStageId(value: string): value is StageId {
  return (STAGE_ORDER as readonly string[]).includes(value);
}
